/**
 * User management API schemas
 */

import { z } from 'zod';
import { Role, Status } from '../identity-contracts';
import { PositiveIntSchema } from './common.schemas';
import {
    PermissionGrantSchema,
    RoleAssignmentDetailedSchema,
    RoleAssignmentPermissionsViewSchema,
} from '../permission.types';
import { RoleAssignmentCustomConfigSchema } from '../backend_config.types';
import { UserEmailIdentitySchema } from '../email-identity';
import type {
    AssignRoleRequest,
    ProvisionOrganizationalUserCreatedUser,
    ProvisionOrganizationalUserRequest,
    ProvisionOrganizationalUserResponse,
    ProvisionInternalUserCreatedUser,
    ProvisionInternalUserResponse,
    ProvisionUserRequest,
    RevokeRoleRequest,
    RequestAddEmailVerificationRequest,
    RequestAddEmailVerificationResponse,
    RequestAddPhoneVerificationRequest,
    RequestAddPhoneVerificationResponse,
    SetUserMfaRequiredRequest,
    SetUserPhonesRequest,
    UserProfileDetailedPublicResponse,
    UserProfilePublicResponse,
    UpdateRoleAssignmentRequest,
    UpdateUserResponse,
    UpdateRoleAssignmentBodyRequest,
    VerifyAddPhoneRequest,
} from '../user.types';

/**
 * Provision user request (unified for both IUser and OUser)
 */
export const ProvisionUserRequestSchema: z.ZodType<ProvisionUserRequest> = z.object({
    primary_email: z.string().email(),
    emails: z.array(z.string().email()),
    role: z.enum(Role),
    organization_id: z.number().nullable(), // Required for organizational roles, null/undefined for internal users
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    skip_verification: z.boolean().optional(),
    policies: z.array(z.string().uuid()).optional(),
});

/**
 * Provision user response
 */
export const ProvisionUserResponseSchema = z.object({
    user: z.lazy(() => UserProfileDetailedResponseSchema).nullable(),
    invitation_token: z.string().nullable(),
    invitation_expires_at: z.string().nullable(), // ISO date string
    primary_email: z.string().email(),
});

export const ProvisionInternalUserCreatedUserSchema: z.ZodType<ProvisionInternalUserCreatedUser> =
    z.object({
        user_uuid: z.string().uuid(),
    });

export const ProvisionInternalUserResponseSchema: z.ZodType<ProvisionInternalUserResponse> = z.object({
    user: ProvisionInternalUserCreatedUserSchema.nullable(),
    invitation_token: z.string().nullable(),
    invitation_expires_at: z.string().nullable(), // ISO date string
    primary_email: z.string().email(),
});

export const ProvisionOrganizationalUserRequestSchema: z.ZodType<ProvisionOrganizationalUserRequest> =
    z.object({
        primary_email: z.string().email(),
        emails: z.array(z.string().email()),
        role: z.enum(Role),
        organization_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        skip_verification: z.boolean().optional(),
        policies: z.array(z.string().uuid()).optional(),
    });

export const ProvisionOrganizationalUserCreatedUserSchema: z.ZodType<ProvisionOrganizationalUserCreatedUser> =
    z.object({
        user_uuid: z.string().uuid(),
    });

export const ProvisionOrganizationalUserResponseSchema: z.ZodType<ProvisionOrganizationalUserResponse> =
    z.object({
        user: ProvisionOrganizationalUserCreatedUserSchema.nullable(),
        invitation_token: z.string().nullable(),
        invitation_expires_at: z.string().nullable(), // ISO date string
        primary_email: z.string().email(),
    });

/**
 * Update user request
 */
export const UpdateUserRequestSchema = z.object({
    primary_email: z.string().email().optional(),
    status: z.enum(Status).optional(),
    first_name: z.string().nullable().optional(),
    last_name: z.string().nullable().optional(),
});

export const UpdateUserResponseSchema: z.ZodType<UpdateUserResponse> = z.object({
    user_uuid: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    phones: z.array(z.string()).optional(),
    primary_phone: z.string().nullable().optional(),
    totp_enabled: z.boolean(),
    status: z.enum(Status),
    invitation_token: z.string().optional(),
});

/**
 * User profile response (basic, without role assignments)
 */
export const UserProfileResponseSchema = z.object({
    user_id: z.number(),
    user_uuid: z.string().uuid().optional(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    phones: z.array(z.string()).default([]),
    primary_phone: z.string().nullable().default(null),
    totp_enabled: z.boolean(),
    status: z.enum(Status),
    invitation_token: z.string().optional(),
});

/**
 * User profile response with full details (includes role assignments)
 */
export const UserProfileDetailedResponseSchema = UserProfileResponseSchema.extend({
    default_role_assignment: RoleAssignmentDetailedSchema.nullable(),
    other_role_assignments: z.array(RoleAssignmentDetailedSchema),
});

export const UserProfilePublicResponseSchema: z.ZodType<UserProfilePublicResponse> = z.object({
    user_uuid: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string(),
    emails: z.array(z.string()),
    phones: z.array(z.string()).optional(),
    primary_phone: z.string().nullable().optional(),
    totp_enabled: z.boolean(),
    mfa_required: z.boolean(),
    mfa_email_allowed: z.boolean(),
    mfa_sms_allowed: z.boolean(),
    status: z.enum(Status),
    invitation_token: z.string().optional(),
});

export const UserProfileDetailedPublicResponseSchema: z.ZodType<UserProfileDetailedPublicResponse> =
    z.object({
        user_uuid: z.string().uuid(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        primary_email: z.string(),
        emails: z.array(z.string()),
        phones: z.array(z.string()).optional(),
        primary_phone: z.string().nullable().optional(),
        totp_enabled: z.boolean(),
        mfa_required: z.boolean(),
        mfa_email_allowed: z.boolean(),
        mfa_sms_allowed: z.boolean(),
        status: z.enum(Status),
        invitation_token: z.string().optional(),
        default_role_assignment: RoleAssignmentPermissionsViewSchema.nullable(),
        other_role_assignments: z.array(RoleAssignmentPermissionsViewSchema),
    });

/**
 * Assign role request
 */
export const AssignRoleRequestSchemaBase: z.ZodType<AssignRoleRequest> = z.object({
    user_uuids: z.array(z.string().uuid()).min(1),
    role: z.enum(Role),
    organization_uuid: z.string().uuid().nullable(), // Required for organizational roles, null for global roles
    permissions: z.array(PermissionGrantSchema).optional(), // Optional permissions to grant immediately
});

export const AssignRoleRequestSchema: z.ZodType<AssignRoleRequest> =
    AssignRoleRequestSchemaBase;

/**
 * Revoke role request
 */
export const RevokeRoleRequestSchema: z.ZodType<RevokeRoleRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(), // Cross-check for safety
});

/**
 * Update role assignment request
 */
export const UpdateRoleAssignmentBodyRequestSchema: z.ZodType<UpdateRoleAssignmentBodyRequest> =
    z.object({
        user_uuid: z.string().uuid(), // Cross-check for safety
        role: z.enum(Role).optional(),
        profile_name: z.string().optional(), // Deprecated - not supported in current schema
        user_config: RoleAssignmentCustomConfigSchema.optional(),
    });

export const UpdateRoleAssignmentRequestSchema: z.ZodType<UpdateRoleAssignmentRequest> = z.object({
    role_assignment_uuid: z.string().uuid(),
    user_uuid: z.string().uuid(), // Cross-check for safety
    role: z.enum(Role).optional(),
    profile_name: z.string().optional(), // Deprecated - not supported in current schema
    user_config: RoleAssignmentCustomConfigSchema.optional(),
});

/**
 * Delete my account request (self-delete)
 */
export const DeleteMyAccountRequestSchema = z.object({
    password: z.string().min(1),
});

/**
 * Delete my account response
 */
export const DeleteMyAccountResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});

/**
 * Get profile response (simplified user profile)
 */
export const GetProfileResponseSchema = z.object({
    user: z
        .object({
            user_uuid: z.string().uuid(),
            primary_email: z.string(),
            emails: z.array(z.string()),
            status: z.enum(Status),
            totp_enabled: z.boolean(),
        })
        .nullable(),
});

/**
 * Update profile request
 */
export const UpdateProfileRequestSchema = z.object({
    primary_email: z.string().email(),
});

/**
 * Reset password admin request
 */
export const ResetPasswordAdminRequestSchema = z.object({
    new_password: z.string().min(1),
});

/**
 * Request password reset admin request
 */
export const RequestPasswordResetAdminRequestSchema: z.ZodObject<{
    email: z.ZodString;
}> = z.object({
    email: z.string().email(),
});

/**
 * Set user MFA requirement request
 */
export const SetUserMfaRequiredRequestSchema: z.ZodType<SetUserMfaRequiredRequest> =
    z.object({
        required: z.boolean(),
    });

/**
 * Set user emails request
 */
export const SetUserEmailsRequestSchema = z
    .object({
        emails: z.array(z.string().email()).min(1),
        primary_email: z.string().email(),
    })
    .superRefine((value, context) => {
        const uniqueEmails = new Set<string>(value.emails);
        if (uniqueEmails.size !== value.emails.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'emails must be unique',
                path: ['emails'],
            });
        }
        if (!uniqueEmails.has(value.primary_email)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'primary_email must be included in emails',
                path: ['primary_email'],
            });
        }
    });

export const UserPhoneNumberInputSchema: z.ZodType<string> = z.string().trim().min(1);

/**
 * Set user phone numbers request
 */
export const SetUserPhonesRequestSchema: z.ZodType<SetUserPhonesRequest> = z
    .object({
        phones: z.array(UserPhoneNumberInputSchema),
        primary_phone: UserPhoneNumberInputSchema.nullable(),
    })
    .superRefine((value, context) => {
        const uniquePhones = new Set<string>(value.phones);
        if (uniquePhones.size !== value.phones.length) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'phones must be unique',
                path: ['phones'],
            });
        }
        if (value.primary_phone !== null && !uniquePhones.has(value.primary_phone)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'primary_phone must be included in phones',
                path: ['primary_phone'],
            });
        }
        if (value.primary_phone === null && value.phones.length > 0) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'primary_phone is required when phones are present',
                path: ['primary_phone'],
            });
        }
    });

export const RequestAddEmailVerificationRequestSchema: z.ZodType<RequestAddEmailVerificationRequest> = z.object({
    email: UserEmailIdentitySchema,
    skip_verification: z.boolean().optional(),
});

export const RequestAddEmailVerificationResponseSchema: z.ZodType<RequestAddEmailVerificationResponse> = z.object({
    message: z.string(),
    verification_link: z.string().optional(),
});

export const RequestAddPhoneVerificationRequestSchema: z.ZodType<RequestAddPhoneVerificationRequest> =
    z.object({
        phone: UserPhoneNumberInputSchema,
        skip_verification: z.boolean().optional(),
    });

export const RequestAddPhoneVerificationResponseSchema: z.ZodType<RequestAddPhoneVerificationResponse> =
    z.object({
        message: z.string(),
        delivery_destination: z.string().optional(),
        expires_at: z.string().optional(),
    });

export const VerifyAddPhoneRequestSchema: z.ZodType<VerifyAddPhoneRequest> = z.object({
    phone: UserPhoneNumberInputSchema,
    code: z.string().trim().min(1),
});

/**
 * Get emails response
 */
export const GetEmailsResponseSchema = z.object({
    primary_email: z.string(),
    emails: z.array(
        z.object({
            email: z.string(),
            is_primary: z.boolean(),
            is_verified: z.boolean(),
        })
    ),
});

/**
 * Get my roles response
 */
export const GetMyRolesResponseSchema = z.object({
    roles: z.array(
        z.object({
            role: z.enum(Role),
            organization_uuid: z.string().uuid().nullable(),
            organization_name: z.string().optional(),
        })
    ),
});

/**
 * Common param schemas
 */
export const UserIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const UserUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const RoleAssignmentIdParamSchema: z.ZodType<number> = PositiveIntSchema;
